import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
  setDoc,
  onSnapshot,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import {
  FileText, DollarSign, PieChart,
  Trash2, Download, Plus, Check, X, Loader2,
  Info, Globe, Settings, AlertCircle, Home, Clock,
  Calendar, Filter, Edit2, TrendingUp, TrendingDown,
  Sparkles, Calculator, Briefcase, Coins, Building,
  MessageSquare, User, RefreshCw, AlertTriangle, Menu, LayoutDashboard,
  LogOut, ChevronRight, Laptop, Mail, ShieldCheck, CreditCard, Wallet,
  ArrowUpRight, ArrowDownLeft, Lock, Crown, BookOpen, Shield, UploadCloud, File, Square, CheckSquare
} from 'lucide-react';
import {
  PieChart as RePieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend
} from 'recharts';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

// Worker setup for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ==========================================
// 1. YOUR FIREBASE CONFIGURATION
// ==========================================
const firebaseConfig = {
  apiKey: "AIzaSyAyuaMKVOjrGFkrnV1bDLWSxnXzVU5Kl7Q",
  authDomain: "snaptax-40fe0.firebaseapp.com",
  projectId: "snaptax-40fe0",
  storageBucket: "snaptax-40fe0.firebasestorage.app",
  messagingSenderId: "571367830808",
  appId: "1:571367830808:web:4698891ded748836aa9a6d",
  measurementId: "G-VJW3X9M27B"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = "snaptax-production-v2";

// ==========================================
// CONFIGURATION
// ==========================================
const SUPPORT_EMAIL = "nastrades7@gmail.com";
const SELAR_LINK = "https://selar.com/zsd18gv315"; 

const TRIAL_DURATION_DAYS = 7; 
const THRESHOLD_SMALL_BIZ = 50000000; 
const VAT_RATE = 0.075; 
const COUNTRY_DATA = { "Nigeria": { code: "NGN", symbol: "₦", name: "Nigerian Naira" } };
const CATEGORIES = {
    expense: ['Operations', 'Salaries', 'Rent', 'Utilities', 'Marketing', 'Software', 'Travel', 'Cost of Goods'],
    income: ['Sales', 'Services', 'Consulting', 'Investments', 'Other']
};

// ==========================================
// HELPER COMPONENTS
// ==========================================
const Loader = ({ className }) => <Loader2 className={className || "animate-spin"} />;

const Notification = ({ message, type = 'info', onClose }) => (
  <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[100] max-w-[90%] w-full sm:max-w-md bg-white p-4 rounded-xl shadow-2xl border-l-4 ${type === 'error' ? 'border-red-500' : type === 'success' ? 'border-emerald-500' : 'border-blue-500'} flex items-start gap-3 animate-in fade-in slide-in-from-top-4`}>
    <div className={`mt-1 p-1 rounded-full ${type === 'error' ? 'bg-red-100 text-red-600' : type === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
      {type === 'error' ? <AlertCircle size={16} /> : type === 'success' ? <Check size={16} /> : <Info size={16} />}
    </div>
    <div className="flex-1">
        <p className="text-sm font-bold text-slate-800">{type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Note'}</p>
        <p className="text-sm font-medium text-slate-600 leading-tight">{message}</p>
    </div>
    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
  </div>
);

const formatCurrency = (val) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(val || 0);

const StatCard = ({ title, value, icon: Icon, colorClass = 'bg-indigo-600', subtext }) => (
  <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-start justify-between">
    <div>
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{value}</h3>
      {subtext && <p className="text-[10px] sm:text-xs text-slate-400 mt-1">{subtext}</p>}
    </div>
    <div className={`p-3 rounded-xl ${colorClass} text-white bg-opacity-90 shadow-sm`}>
        <Icon size={20} />
    </div>
  </div>
);

// ==========================================
// CALCULATOR
// ==========================================
const CalculatorScreen = () => {
  const [income, setIncome] = useState('');
  const calculation = useMemo(() => {
    const annualIncome = parseFloat(income) || 0;
    if (annualIncome <= 800000) return { tax: 0, rate: '0% (Exempt)', net: annualIncome };
    let taxable = annualIncome; 
    const relief = 200000 + (annualIncome * 0.20); 
    taxable = Math.max(0, taxable - relief);
    let tax = 0, temp = taxable;
    const bands = [{ limit: 300000, rate: 0.07 }, { limit: 300000, rate: 0.11 }, { limit: 500000, rate: 0.15 }, { limit: 500000, rate: 0.19 }, { limit: 1600000, rate: 0.21 }, { limit: Infinity, rate: 0.24 }];
    for (let band of bands) {
        if (temp <= 0) break;
        const taxableAmount = Math.min(temp, band.limit);
        tax += taxableAmount * band.rate;
        temp -= taxableAmount;
    }
    return { tax, rate: 'Progressive (7-24%)', net: annualIncome - tax };
  }, [income]);

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in pb-20">
      <div className="bg-slate-900 p-6 rounded-3xl text-white mb-6 shadow-xl">
        <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-500 rounded-lg"><Calculator size={20} /></div>
            <h1 className="text-xl sm:text-2xl font-bold">Personal Income Tax</h1>
        </div>
        <p className="text-slate-400 text-sm">For Salary Earners (2025 Act)</p>
      </div>
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-6">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Annual Gross Salary</label>
            <div className="relative">
                <span className="absolute left-4 top-4 text-slate-400 font-bold">₦</span>
                <input type="number" className="w-full p-4 pl-10 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-base" placeholder="Enter annual income..." value={income} onChange={(e) => setIncome(e.target.value)} />
            </div>
          </div>
          <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex justify-between items-center text-sm"><span className="text-slate-600">Tax Rate Band</span><span className="font-bold text-indigo-600">{calculation.rate}</span></div>
              <div className="flex justify-between items-center text-sm"><span className="text-slate-600">Estimated Annual Tax</span><span className="font-bold text-red-600">{formatCurrency(calculation.tax)}</span></div>
              <div className="bg-emerald-50 p-4 rounded-xl flex justify-between items-center border border-emerald-100"><span className="text-emerald-800 text-sm font-bold">Net Take Home</span><span className="font-bold text-emerald-700 text-lg">{formatCurrency(calculation.net)}</span></div>
          </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN APP COMPONENT
// ==========================================
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [userProfile, setUserProfile] = useState({ name: '', email: '' });
  
  // State for Trial/Premium
  const [isPremium, setIsPremium] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(7);
  const [isLocked, setIsLocked] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Selection State
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Info Modals
  const [showGuide, setShowGuide] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // UI State
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingId, setEditingId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ description: '', amount: '', type: 'expense', category: 'Operations', date: new Date().toISOString().split('T')[0], isVatable: false, note: '' });
  const [feedback, setFeedback] = useState('');

  const currentYear = new Date().getFullYear();

  // AUTH & DATA
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    const userRef = doc(db, 'artifacts', appId, 'users', user.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.displayName) setUserProfile({ name: data.displayName, email: data.displayEmail });
        
        const userIsPremium = data.subscriptionStatus === 'premium';
        setIsPremium(userIsPremium);

        if (!userIsPremium && data.joinedAt) {
            const joinedDate = data.joinedAt.toDate();
            const diffTime = Math.abs(new Date() - joinedDate);
            const daysUsed = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
            const remaining = TRIAL_DURATION_DAYS - daysUsed;
            
            setTrialDaysLeft(Math.max(0, remaining));
            if (remaining <= 0) { setIsLocked(true); setShowPaywall(true); }
        } else if (!userIsPremium && !data.joinedAt) {
             updateDoc(userRef, { joinedAt: serverTimestamp() });
        }
      } else {
        setDoc(userRef, { joinedAt: serverTimestamp(), country: "Nigeria", displayName: user.displayName || 'Guest', displayEmail: user.email || '' });
      }
    });

    const txRef = collection(db, 'artifacts', appId, 'users', user.uid, 'transactions');
    const unsubTx = onSnapshot(txRef, (snap) => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const pendingRef = localStorage.getItem('snaptax_pending_ref');
    if (pendingRef) verifyPayment(pendingRef);

    return () => { unsub(); unsubTx(); };
  }, [user]);

  // LOGIC
  const stats = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => new Date(b.date) - new Date(a.date));
    const yearTx = sorted.filter(t => new Date(t.date).getFullYear() === currentYear);
    
    const totalIncomeYTD = yearTx.filter(t => t.type === 'income').reduce((sum, t) => sum + Number(t.amount), 0);
    const totalExpenseYTD = yearTx.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount), 0);
    const profitYTD = Math.max(0, totalIncomeYTD - totalExpenseYTD);
    const isExempt = totalIncomeYTD < THRESHOLD_SMALL_BIZ;
    const estimatedCIT = isExempt ? 0 : profitYTD * 0.30; 
    const thresholdProgress = Math.min((totalIncomeYTD / THRESHOLD_SMALL_BIZ) * 100, 100);
    const totalVatYTD = yearTx.filter(t => t.type === 'income' && t.isVatable).reduce((sum, t) => sum + (Number(t.amount) * VAT_RATE), 0);
    const totalTaxLiability = totalVatYTD + estimatedCIT;
    const expenseData = CATEGORIES.expense.map(cat => ({ name: cat, value: yearTx.filter(t => t.type === 'expense' && t.category === cat).reduce((s, t) => s + Number(t.amount), 0) })).filter(i => i.value > 0);

    return { totalIncomeYTD, totalExpenseYTD, profitYTD, thresholdProgress, isExempt, estimatedCIT, totalVatYTD, totalTaxLiability, sorted, expenseData };
  }, [transactions, currentYear]);

  // HANDLERS
  const handleLogin = async () => {
      setLoggingIn(true);
      try { 
          const provider = new GoogleAuthProvider();
          await signInWithPopup(auth, provider);
      } catch (e) { 
          let msg = "Login failed.";
          if (e.code === 'auth/unauthorized-domain') msg = "Domain not authorized in Firebase.";
          setNotification({ message: msg, type: 'error'}); 
          setLoggingIn(false);
      }
  };

  const handleGuestLogin = async () => {
      setLoggingIn(true);
      try { await signInAnonymously(auth); } catch (e) { setLoggingIn(false); }
  };

  const handleLogout = async () => {
      await signOut(auth);
      setUser(null);
  };

  const handleSave = async () => {
    if (isLocked && !isPremium) { setShowPaywall(true); return; }
    if (!formData.description || !formData.amount) {
      setNotification({ message: 'Missing details.', type: 'error' });
      return;
    }
    setIsSubmitting(true);
    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 5000));
    try {
        const colRef = collection(db, 'artifacts', appId, 'users', user.uid, 'transactions');
        const data = { ...formData, amount: parseFloat(formData.amount) };
        if (editingId) await updateDoc(doc(colRef, editingId), data);
        else await addDoc(colRef, { ...data, createdAt: serverTimestamp() });
        setNotification({ message: 'Saved successfully.', type: 'success' });
        setFormData({ description: '', amount: '', type: 'expense', category: 'Operations', date: new Date().toISOString().split('T')[0], isVatable: false, note: '' });
        setEditingId(null);
        setActiveTab('dashboard');
    } catch (e) { setNotification({ message: 'Error saving.', type: 'error' }); } 
    finally { setIsSubmitting(false); }
  };

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleAll = () => {
    if (selectedIds.size === stats.sorted.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(stats.sorted.map(t => t.id)));
  };

  const deleteSelected = async () => {
      if (!window.confirm(`Delete ${selectedIds.size} transactions?`)) return;
      const batch = writeBatch(db);
      selectedIds.forEach(id => {
          const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'transactions', id);
          batch.delete(ref);
      });
      try {
          await batch.commit();
          setSelectedIds(new Set());
          setNotification({ message: 'Deleted successfully.', type: 'success' });
      } catch (e) { setNotification({ message: 'Delete failed.', type: 'error' }); }
  };

  const clearAllHistory = async () => {
      if (!window.confirm("WARNING: This will permanently delete ALL your transaction history. Continue?")) return;
      const allIds = stats.sorted.map(t => t.id);
      const chunkSize = 500;
      try {
          for (let i = 0; i < allIds.length; i += chunkSize) {
              const batch = writeBatch(db);
              const chunk = allIds.slice(i, i + chunkSize);
              chunk.forEach(id => {
                  const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'transactions', id);
                  batch.delete(ref);
              });
              await batch.commit();
          }
          setNotification({ message: 'All history cleared.', type: 'success' });
          setSelectedIds(new Set());
      } catch (e) { setNotification({ message: 'Clear all failed.', type: 'error' }); }
  };

  const startPayment = async () => {
      setProcessingPayment(true);
      const refId = `${user.uid}_${Date.now()}`;
      try {
          await setDoc(doc(db, 'artifacts', appId, 'payments', refId), {
              uid: user.uid, ref: refId, status: 'pending', createdAt: serverTimestamp(), amount: 5000
          });
          localStorage.setItem('snaptax_pending_ref', refId);
          window.location.href = `${SELAR_LINK}?ref=${refId}`;
      } catch (e) { window.location.href = `${SELAR_LINK}?ref=${refId}`; }
  };

  const verifyPayment = async (refId) => {
      setVerifyingPayment(true);
      setTimeout(async () => {
          try {
              const pRef = doc(db, 'artifacts', appId, 'payments', refId);
              try { await updateDoc(pRef, { status: 'paid' }); } catch(e){}
              await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid), { subscriptionStatus: 'premium' });
              setIsPremium(true); setIsLocked(false); setShowPaywall(false);
              localStorage.removeItem('snaptax_pending_ref');
              setNotification({ message: "Premium Activated! Thank you.", type: 'success'});
          } catch (e) { console.error(e); } 
          finally { setVerifyingPayment(false); }
      }, 2000);
  };

  const processImportText = (text, batch, count) => {
      const lines = text.split(/\r?\n/);
      lines.forEach(line => {
          if (!line || line.length < 5) return;
          const dateMatch = line.match(/\b\d{1,2}[-/\. ](?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[-/\. ]?\d{2,4}\b/i) || 
                            line.match(/\b\d{1,2}[-/\.]\d{1,2}[-/\.]\d{2,4}\b/) ||
                            line.match(/\b\d{4}[-/\.]\d{1,2}[-/\.]\d{1,2}\b/);
          const cleanLine = line.replace(/"/g, '').replace(/'/g, ''); 
          const numbers = cleanLine.match(/[\d,]+\.\d{2}/g); 
          
          if (dateMatch && numbers) {
              const dateStr = dateMatch[0];
              const yearVal = new Date(dateStr).getFullYear();
              const validAmounts = numbers.filter(n => parseFloat(n.replace(/,/g, '')) !== yearVal);
              
              if (validAmounts.length > 0) {
                  const rawAmount = validAmounts[0].replace(/,/g, '');
                  const amount = parseFloat(rawAmount);

                  if (!isNaN(amount) && amount > 0) {
                      const lowerLine = cleanLine.toLowerCase();
                      let type = 'expense';
                      if (lowerLine.includes('cr') || lowerLine.includes('credit') || lowerLine.includes('deposit') || 
                          lowerLine.includes('inward') || lowerLine.includes('interest earned') || lowerLine.includes('transfer from') || lowerLine.includes('owealth withdrawal')) {
                          type = 'income';
                      }
                      if (lowerLine.includes('dr') || lowerLine.includes('debit') || lowerLine.includes('withdrawal') || 
                          lowerLine.includes('transfer to') || lowerLine.includes('purchase') || lowerLine.includes('payment') ||
                          lowerLine.includes('pos') || lowerLine.includes('web') || lowerLine.includes('mobile data') || lowerLine.includes('betting')) {
                          type = 'expense';
                      }
                      let cat = 'Other';
                      if(lowerLine.includes('airtime') || lowerLine.includes('data') || lowerLine.includes('mtn') || lowerLine.includes('glo') || lowerLine.includes('9mobile') || lowerLine.includes('nepa') || lowerLine.includes('disco')) cat = 'Utilities';
                      if(lowerLine.includes('food') || lowerLine.includes('restaurant') || lowerLine.includes('kitchen') || lowerLine.includes('buka')) cat = 'Meals';
                      if(lowerLine.includes('uber') || lowerLine.includes('bolt') || lowerLine.includes('fuel') || lowerLine.includes('filling') || lowerLine.includes('transport')) cat = 'Transport';
                      
                      let cleanDesc = line.replace(dateStr, '').replace(validAmounts[0], '').replace(/[^\w\s-]/gi, '').trim();
                      if(cleanDesc.length > 40) cleanDesc = cleanDesc.substring(0, 40) + '...';
                      if(cleanDesc.length < 3) cleanDesc = "Imported Transaction";

                      const newRef = doc(collection(db, 'artifacts', appId, 'users', user.uid, 'transactions'));
                      batch.set(newRef, {
                          date: new Date(dateStr).toISOString().split('T')[0],
                          description: cleanDesc,
                          amount: amount,
                          type: type,
                          category: cat,
                          isVatable: false,
                          createdAt: serverTimestamp()
                      });
                      count.val++;
                  }
              }
          }
      });
  };

  const handleFileUpload = async (event) => {
      if (!isPremium) { setShowPaywall(true); return; }
      const file = event.target.files[0];
      if (!file) return;

      setImporting(true);
      const batch = writeBatch(db);
      let count = { val: 0 };

      try {
          if (file.type === "application/pdf") {
             const buffer = await file.arrayBuffer();
             const pdf = await pdfjsLib.getDocument(buffer).promise;
             let fullText = "";
             const maxPages = Math.min(pdf.numPages, 5); 
             for (let i = 1; i <= maxPages; i++) {
                 const page = await pdf.getPage(i);
                 const textContent = await page.getTextContent();
                 const pageText = textContent.items.map(item => item.str).join(' '); 
                 fullText += pageText + "\n";
             }
             processImportText(fullText, batch, count);
          } else {
             const text = await file.text();
             processImportText(text, batch, count);
          }

          if (count.val > 0) {
              await batch.commit();
              setNotification({ message: `Successfully imported ${count.val} transactions!`, type: 'success' });
              setActiveTab('dashboard');
          } else {
              setNotification({ message: "No clear transactions found. Ensure it's a digital statement.", type: 'error' });
          }
      } catch(e) {
          console.error(e);
          setNotification({ message: "Error reading file. Is it a scanned image?", type: 'error' });
      }
      setImporting(false);
  };

  const handleExportCSV = () => {
      if (isLocked && !isPremium) { setShowPaywall(true); return; }
      const headers = ['Date', 'Type', 'Description', 'Category', 'Amount', 'Vatable', 'Note'];
      const csv = [headers.join(','), ...stats.sorted.map(t => 
          [t.date, t.type, `"${t.description}"`, t.category, t.amount, t.isVatable ? 'Yes' : 'No', `"${t.note||''}"`].join(',')
      )].join('\n');
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      link.download = `snaptax_2025.csv`;
      link.click();
  };

  const handleSendFeedback = async () => {
      if(!feedback.trim()) return;
      setIsSubmitting(true);
      try {
          await addDoc(collection(db, 'artifacts', appId, 'feedback'), { uid: user.uid, message: feedback, createdAt: serverTimestamp() });
          setFeedback(''); setNotification({ message: 'Feedback sent!', type: 'success'});
      } catch (e) { setNotification({ message: 'Error sending feedback.', type: 'error'}); } 
      finally { setIsSubmitting(false); }
  };

  // -- VIEWS --
  if (authLoading) return <div className="h-screen flex items-center justify-center bg-slate-50"><Loader className="w-10 h-10 text-slate-900 animate-spin" /></div>;

  if (!user) return (
      <div className="min-h-screen bg-white flex flex-col justify-center items-center p-6 text-center">
          <div className="mb-8 p-4 bg-indigo-600 rounded-3xl shadow-xl shadow-indigo-200 rotate-3"><LayoutDashboard size={48} className="text-white" /></div>
          <h1 className="text-4xl font-extrabold text-slate-900 mb-2">SnapTax</h1>
          <p className="text-slate-500 mb-8 max-w-xs">Compliance with Nigeria Tax Act 2025 made simple.</p>
          <div className="w-full max-w-xs space-y-3">
              <button onClick={handleLogin} disabled={loggingIn} className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-lg transition-all flex justify-center items-center gap-3 shadow-lg">
                  {loggingIn ? <Loader className="animate-spin" /> : <>Sign in with Google</>}
              </button>
              <button onClick={handleGuestLogin} disabled={loggingIn} className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-sm transition-all">Continue as Guest</button>
          </div>
          <p className="text-xs text-slate-400 mt-4">7 Days Free Trial • Then ₦5,000/month</p>
      </div>
  );

  return (
    <div className={`min-h-screen bg-slate-50 text-slate-900 font-sans pb-32 md:pb-0 ${isLocked ? 'overflow-hidden h-screen' : ''}`}>
      {notification && <Notification {...notification} onClose={() => setNotification(null)} />}

      {verifyingPayment && (
         <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-6 animate-in fade-in z-[200]">
             <Loader className="w-16 h-16 text-emerald-500 animate-spin mb-4" />
             <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h2>
             <p className="text-slate-400 text-center max-w-sm">Please wait while we confirm your transaction.</p>
         </div>
      )}

      {showGuide && (
          <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl max-h-[80vh] flex flex-col">
                  <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                      <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><BookOpen size={20} className="text-indigo-600"/> How to Use</h3>
                      <button onClick={() => setShowGuide(false)}><X size={20} className="text-slate-400"/></button>
                  </div>
                  <div className="p-6 overflow-y-auto space-y-4 text-sm text-slate-600">
                      <p><b>1. Sign In & Sync:</b> Create an account with Google to back up your data securely.</p>
                      <p><b>2. Record Transactions:</b> Tap the <b>Record (+)</b> button. Select 'Expense' for costs or 'Income' for sales.</p>
                      <p><b>3. Monitor Status:</b> The Dashboard shows if you are exempt from CIT (Turnover &lt; ₦50m) or liable.</p>
                      <p><b>4. Import:</b> (Premium) Upload your Bank Statement (PDF or CSV) to auto-fill transactions.</p>
                  </div>
              </div>
          </div>
      )}

      {showPrivacy && (
          <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl max-h-[80vh] flex flex-col">
                  <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                      <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><Shield size={20} className="text-emerald-600"/> Privacy Policy</h3>
                      <button onClick={() => setShowPrivacy(false)}><X size={20} className="text-slate-400"/></button>
                  </div>
                  <div className="p-6 overflow-y-auto space-y-4 text-sm text-slate-600">
                      <p><b>Data Collection:</b> We collect your name, email, and transaction data solely to provide tax calculations.</p>
                      <p><b>Security:</b> Your data is encrypted and stored on Google Firebase servers.</p>
                      <p><b>Payments:</b> We use Selar for payments. We do not store your credit card details.</p>
                      <p><b>Your Rights:</b> You own your data. You can export it or request deletion at any time.</p>
                  </div>
              </div>
          </div>
      )}

      {showPaywall && !verifyingPayment && (
          <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
              <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl relative">
                  {!isLocked && <button onClick={() => setShowPaywall(false)} className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full"><X size={20}/></button>}
                  <div className="bg-indigo-600 p-8 text-center text-white">
                      <Crown size={48} className="mx-auto mb-4"/>
                      <h2 className="text-2xl font-bold">{isLocked ? "Trial Expired" : "Upgrade to Pro"}</h2>
                      <p className="text-indigo-100 text-sm mt-1">{isLocked ? "Your 7-day free trial has ended." : "Unlock full access for 1 month."}</p>
                  </div>
                  <div className="p-8 space-y-6">
                      <div className="space-y-3 text-sm text-slate-600">
                          <div className="flex items-center gap-3"><Check size={16} className="text-emerald-500"/> Unlimited Transactions</div>
                          <div className="flex items-center gap-3"><Check size={16} className="text-emerald-500"/> 2025 Tax Act Compliance</div>
                          <div className="flex items-center gap-3"><Check size={16} className="text-emerald-500"/> Import Bank Statements (PDF/CSV)</div>
                      </div>
                      <button onClick={startPayment} disabled={processingPayment} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold text-lg shadow-xl flex justify-center gap-2">
                          {processingPayment ? <Loader className="animate-spin"/> : "Pay ₦5,000 Now"}
                      </button>
                      <p className="text-center text-xs text-slate-400">Secure payment via Selar.</p>
                  </div>
              </div>
          </div>
      )}

      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveTab('dashboard')}>
                  <div className="bg-indigo-600 p-2 rounded-lg text-white"><LayoutDashboard size={20} /></div>
                  <span className="font-bold text-xl hidden sm:block">SnapTax</span>
              </div>
              <div className="flex items-center gap-3">
                  {!isPremium && !isLocked && (
                      <div className="hidden sm:flex flex-col items-end mr-2">
                          <span className="text-[10px] font-bold text-indigo-600 uppercase">{trialDaysLeft} Days Left</span>
                          <div className="w-20 h-1 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-indigo-500" style={{ width: `${(trialDaysLeft/7)*100}%`}}></div></div>
                      </div>
                  )}
                  {!isPremium && <button onClick={() => setShowPaywall(true)} className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold flex items-center gap-1 border border-amber-200"><Crown size={12}/> Upgrade</button>}
                  <button onClick={() => { setEditingId(null); setActiveTab('add'); }} className="hidden md:flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg hover:bg-slate-800 transition-colors"><Plus size={16} /> Record</button>
                  <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-slate-500">{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
              </div>
          </div>
          {mobileMenuOpen && (
              <div className="md:hidden bg-white border-t border-slate-200 p-4 space-y-2 shadow-lg">
                  {['Dashboard', 'History', 'Calc', 'Settings', 'Add'].map((tab) => (
                      <button key={tab} onClick={() => { setActiveTab(tab.toLowerCase()); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium bg-slate-50">{tab}</button>
                  ))}
              </div>
          )}
      </nav>

      {/* MAIN CONTENT */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6 animate-in fade-in">
            <div className="flex flex-col md:flex-row justify-between items-end gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Welcome, {userProfile.name || 'User'}</h1>
                    <p className="text-slate-500 mt-1 flex items-center gap-2"><Globe size={14}/> {currentYear} Tax Year</p>
                </div>
                <div className="w-full md:w-1/3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex justify-between mb-2">
                        <span className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1"><Building size={12}/> Small Business Limit (₦50m)</span>
                        <span className={`text-xs font-bold ${stats.isExempt ? 'text-emerald-600' : 'text-red-600'}`}>{stats.isExempt ? 'Exempt (0%)' : 'Liable (30%)'}</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full transition-all duration-1000 ${stats.thresholdProgress > 80 ? 'bg-amber-500' : 'bg-indigo-600'}`} style={{ width: `${stats.thresholdProgress}%` }} />
                    </div>
                </div>
            </div>

            <div className="bg-slate-900 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-2">
                        <Wallet size={16}/> Total Tax Liability (YTD)
                    </div>
                    <div className="flex flex-col md:flex-row md:items-end gap-2 md:gap-6">
                        <h2 className="text-4xl md:text-5xl font-extrabold">{formatCurrency(stats.totalTaxLiability)}</h2>
                        <span className="text-slate-400 text-sm mb-2">Accumulated {currentYear}</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-4 border-t border-slate-700 pt-4">This represents your <b>VAT collected</b> + <b>Estimated CIT</b>. Keep this amount saved.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="flex justify-between items-start mb-4">
                        <div><p className="text-xs font-bold text-slate-500 uppercase">VAT Liability (YTD)</p></div>
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><FileText size={20}/></div>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(stats.totalVatYTD)}</h3>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="flex justify-between items-start mb-4">
                        <div><p className="text-xs font-bold text-slate-500 uppercase">Company Tax (CIT)</p></div>
                        <div className="p-2 bg-amber-50 text-amber-600 rounded-lg"><Building size={20}/></div>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(stats.estimatedCIT)}</h3>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="flex justify-between items-start mb-4">
                        <div><p className="text-xs font-bold text-slate-500 uppercase">Net Profit (YTD)</p></div>
                        <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Coins size={20}/></div>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(stats.profitYTD)}</h3>
                </div>
            </div>
          </div>
        )}

        {/* ADD TRANSACTION TAB */}
        {activeTab === 'add' && (
          <div className="max-w-xl mx-auto animate-in fade-in">
             <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-slate-900">{editingId ? 'Edit Transaction' : 'Record Transaction'}</h2>
                    {/* IMPORT BUTTON (Premium) */}
                    <button 
                        onClick={() => document.getElementById('fileUpload').click()}
                        className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg border transition-all ${isPremium ? 'border-indigo-100 bg-indigo-50 text-indigo-600 hover:bg-indigo-100' : 'border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed'}`}
                    >
                        <UploadCloud size={16}/> Import PDF/CSV
                    </button>
                    <input 
                        id="fileUpload" 
                        type="file" 
                        accept=".csv, .pdf" 
                        className="hidden" 
                        onChange={handleFileUpload}
                        disabled={!isPremium}
                    />
                </div>
                {importing && <p className="text-xs text-indigo-600 animate-pulse text-center">Reading bank statement...</p>}
                
                <div className="flex p-1 bg-slate-100 rounded-xl">
                    <button onClick={() => setFormData({...formData, type: 'expense'})} className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all ${formData.type === 'expense' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Expense</button>
                    <button onClick={() => setFormData({...formData, type: 'income'})} className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all ${formData.type === 'income' ? 'bg-emerald-500 shadow text-white' : 'text-slate-500'}`}>Income</button>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Description</label>
                    <input className="w-full p-4 bg-slate-50 border-none rounded-xl text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="e.g. Office Rent" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Amount</label>
                        <input type="number" className="w-full p-4 bg-slate-50 border-none rounded-xl text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Date</label>
                        <input type="date" className="w-full p-4 bg-slate-50 border-none rounded-xl text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Category</label>
                    <select className="w-full p-4 bg-slate-50 border-none rounded-xl text-slate-900 outline-none" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                        {CATEGORIES[formData.type].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                {formData.type === 'income' && (
                    <div className="flex items-center gap-3 p-4 border border-emerald-100 bg-emerald-50 rounded-xl cursor-pointer" onClick={() => setFormData({...formData, isVatable: !formData.isVatable})}>
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${formData.isVatable ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 bg-white'}`}>
                            {formData.isVatable && <Check size={14} className="text-white"/>}
                        </div>
                        <div><p className="text-sm font-bold text-slate-900">Apply 7.5% VAT?</p><p className="text-xs text-slate-500">Check this if VAT was collected.</p></div>
                    </div>
                )}
                <button onClick={handleSave} disabled={isSubmitting} className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all active:scale-95 flex justify-center gap-2 ${formData.type === 'income' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}>
                    {isSubmitting ? <Loader className="animate-spin" /> : "Save Record"}
                </button>
             </div>
          </div>
        )}

        {activeTab === 'calc' && <CalculatorScreen />}

        {activeTab === 'history' && (
          <div className="space-y-6 animate-in fade-in">
             <div className="flex justify-between items-center gap-4">
                 <h2 className="text-2xl font-bold text-slate-900">Records</h2>
                 <div className="flex gap-2 flex-wrap justify-end">
                     {selectedIds.size > 0 && (
                         <button onClick={deleteSelected} className="flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 transition-colors">
                             <Trash2 size={14}/> Del ({selectedIds.size})
                         </button>
                     )}
                     <button onClick={handleExportCSV} className="flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100 transition-colors">
                         <Download size={14}/> CSV
                     </button>
                     <button onClick={clearAllHistory} className="flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-600 border border-slate-100 transition-colors">
                         Clear All
                     </button>
                 </div>
             </div>
             
             {/* DESKTOP TABLE */}
             <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="p-4 w-10">
                                <button onClick={toggleAll} className="text-slate-400 hover:text-slate-600">
                                    {selectedIds.size === stats.sorted.length && stats.sorted.length > 0 ? <CheckSquare size={18}/> : <Square size={18}/>}
                                </button>
                            </th>
                            <th className="p-4 text-xs font-bold text-slate-500 uppercase">Date</th>
                            <th className="p-4 text-xs font-bold text-slate-500 uppercase">Description</th>
                            <th className="p-4 text-xs font-bold text-slate-500 uppercase text-right">Amount</th>
                            <th className="p-4 text-xs font-bold text-slate-500 uppercase text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {stats.sorted.map(t => (
                            <tr key={t.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.has(t.id) ? 'bg-indigo-50/50' : ''}`}>
                                <td className="p-4">
                                    <button onClick={() => toggleSelection(t.id)} className={`transition-colors ${selectedIds.has(t.id) ? 'text-indigo-600' : 'text-slate-300 hover:text-slate-400'}`}>
                                        {selectedIds.has(t.id) ? <CheckSquare size={18}/> : <Square size={18}/>}
                                    </button>
                                </td>
                                <td className="p-4 text-sm text-slate-600 whitespace-nowrap">{t.date}</td>
                                <td className="p-4 text-sm text-slate-900 font-medium">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-1.5 h-1.5 rounded-full ${t.type === 'income' ? 'bg-emerald-500' : 'bg-red-500'}`}/>
                                        <span className="line-clamp-1">{t.description}</span>
                                        {t.isVatable && <span className="text-[10px] bg-slate-100 px-1 rounded border border-slate-200 text-slate-500">VAT</span>}
                                    </div>
                                </td>
                                <td className={`p-4 text-sm font-bold text-right whitespace-nowrap ${t.type === 'income' ? 'text-emerald-600' : 'text-slate-900'}`}>{formatCurrency(t.amount)}</td>
                                <td className="p-4 flex justify-center gap-2">
                                    <button onClick={() => { setEditingId(t.id); setFormData(t); setActiveTab('add') }} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"><Edit2 size={16}/></button>
                                </td>
                            </tr>
                        ))}
                        {stats.sorted.length === 0 && (
                            <tr><td colSpan="5" className="p-8 text-center text-slate-400 text-sm">No records found.</td></tr>
                        )}
                    </tbody>
                </table>
             </div>

             {/* MOBILE CARD VIEW */}
             <div className="md:hidden space-y-3">
                {/* Mobile Select All */}
                {stats.sorted.length > 0 && (
                    <div className="flex items-center gap-2 px-2 mb-2">
                        <button onClick={toggleAll} className="flex items-center gap-2 text-xs font-bold text-slate-500">
                             {selectedIds.size === stats.sorted.length ? <CheckSquare size={18} className="text-indigo-600"/> : <Square size={18}/>}
                             Select All
                        </button>
                    </div>
                )}
                
                {stats.sorted.map(t => (
                    <div 
                        key={t.id} 
                        className={`bg-white p-4 rounded-xl shadow-sm border transition-colors relative ${selectedIds.has(t.id) ? 'border-indigo-200 bg-indigo-50/30' : 'border-slate-100'}`}
                        onClick={() => toggleSelection(t.id)} // Make whole card clickable for selection
                    >
                        <div className="flex justify-between items-start">
                            <div className="flex items-start gap-3">
                                {/* Selection Indicator */}
                                <div className={`mt-1 ${selectedIds.has(t.id) ? 'text-indigo-600' : 'text-slate-300'}`}>
                                    {selectedIds.has(t.id) ? <CheckSquare size={18}/> : <Square size={18}/>}
                                </div>
                                
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-bold text-slate-900 line-clamp-1">{t.description}</p>
                                        {t.isVatable && <span className="text-[10px] bg-slate-100 px-1.5 rounded border border-slate-200 text-slate-500 font-bold">VAT</span>}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.type === 'income' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                            {t.type === 'income' ? 'Income' : 'Expense'}
                                        </span>
                                        <p className="text-xs text-slate-400">• {t.date}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="text-right">
                                <p className={`font-bold text-base ${t.type === 'income' ? 'text-emerald-600' : 'text-slate-900'}`}>
                                    {t.type === 'income' ? '+' : '-'}{formatCurrency(t.amount)}
                                </p>
                                <button 
                                    onClick={(e) => { 
                                        e.stopPropagation(); // Don't trigger selection
                                        setEditingId(t.id); setFormData(t); setActiveTab('add'); 
                                    }} 
                                    className="text-indigo-500 p-2 -mr-2 mt-1 inline-block"
                                >
                                    <Edit2 size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {stats.sorted.length === 0 && <p className="text-center text-slate-400 text-sm py-8">No records found.</p>}
             </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto animate-in fade-in space-y-6">
             <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                 <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><User size={20}/> User Profile</h3>
                 <div className="space-y-4">
                     <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-600">Signed in as: <b>{userProfile.email || 'Guest'}</b></div>
                     <button onClick={handleLogout} className="w-full p-4 rounded-xl border border-red-100 bg-red-50 text-red-600 font-bold flex items-center justify-center gap-2 hover:bg-red-100 transition-colors"><LogOut size={18} /> Sign Out</button>
                 </div>
             </div>
             
             {/* LINKS TO MODALS */}
             <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-4">
                 <h3 className="font-bold text-slate-800 flex items-center gap-2"><Info size={20}/> About & Legal</h3>
                 <button onClick={() => setShowGuide(true)} className="w-full p-4 bg-slate-50 rounded-xl flex justify-between items-center text-sm font-medium text-slate-700 hover:bg-slate-100">
                     <span className="flex items-center gap-3"><BookOpen size={18} className="text-indigo-600"/> How to Use SnapTax</span>
                     <ChevronRight size={16} className="text-slate-400"/>
                 </button>
                 <button onClick={() => setShowPrivacy(true)} className="w-full p-4 bg-slate-50 rounded-xl flex justify-between items-center text-sm font-medium text-slate-700 hover:bg-slate-100">
                     <span className="flex items-center gap-3"><Shield size={18} className="text-emerald-600"/> Privacy Policy</span>
                     <ChevronRight size={16} className="text-slate-400"/>
                 </button>
             </div>

             <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                 <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><MessageSquare size={20}/> Support</h3>
                 <textarea className="w-full p-3 bg-slate-50 rounded-xl mb-4 border border-slate-200" rows="3" placeholder="Feedback?" value={feedback} onChange={e => setFeedback(e.target.value)}></textarea>
                 <button onClick={handleSendFeedback} disabled={isSubmitting} className="bg-indigo-50 text-indigo-600 px-6 py-2 rounded-lg text-sm font-bold">Send</button>
             </div>
          </div>
        )}
      </main>

      <div className="md:hidden fixed bottom-0 w-full bg-white border-t border-slate-200 p-2 flex justify-around z-50 pb-safe">
          <button onClick={() => setActiveTab('dashboard')} className={`flex flex-col items-center p-2 ${activeTab === 'dashboard' ? 'text-indigo-600' : 'text-slate-400'}`}><Home size={24}/></button>
          <button onClick={() => setActiveTab('history')} className={`flex flex-col items-center p-2 ${activeTab === 'history' ? 'text-indigo-600' : 'text-slate-400'}`}><Clock size={24}/></button>
          <div className="relative -top-6"><button onClick={() => { setEditingId(null); setActiveTab('add'); }} className="bg-slate-900 text-white p-4 rounded-full shadow-lg"><Plus size={28}/></button></div>
          <button onClick={() => setActiveTab('calc')} className={`flex flex-col items-center p-2 ${activeTab === 'calc' ? 'text-indigo-600' : 'text-slate-400'}`}><Calculator size={24}/></button>
          <button onClick={() => setActiveTab('settings')} className={`flex flex-col items-center p-2 ${activeTab === 'settings' ? 'text-indigo-600' : 'text-slate-400'}`}><Settings size={24}/></button>
      </div>
    </div>
  );
}


